import TagManager, { DataLayerArgs, TagManagerArgs } from 'react-gtm-module';
import { ToolName } from 'types/AppState';
import { ExportFinishedData, Resolution, Result } from 'types/Downloading';
import { TransitionType } from 'types/Transitions';
import { omit } from 'ramda';
import { TargetSettingsAspectRatioEnum } from 'constants/ratioFormats';
import { ScalingType } from 'types/Scene';
import AnalyticsService from '@picsart/rc/services/analytics';
import { VideoFileContainerEnum } from 'api';

type Transition = 'none' | 'fade' | 'wipe' | 'shape';

const GTM_ID = 'GTM-PQ45W6W';
const GTM_MODULE_ID = 'gold';

export enum Shortcuts {
  SceneCopy = 'scene_copy',
  ScenePaste = 'scene_paste',
  SceneDelete = 'scene_delete',
  TextCopy = 'text_copy',
  TextCut = 'text_cut',
  TextPaste = 'text_paste',
  TextDelete = 'text_delete',
  TextMove = 'text_move',
  Unselect = 'unselect',
  Play = 'play',
  Pause = 'pause',
  Undo = 'undo',
  Redo = 'redo'
}
export const ToolDetailsName = 'add_video';

export type ToolDetails = {
  name: string;
  id: string;
  is_premium: boolean;
  is_shutterstock?: boolean;
  item: 'text' | 'music_tool' | 'photo' | 'video' | 'template';
};

export type ContentSource =
  | 'stock_videos'
  | 'stock_images'
  | 'templates'
  | 'upload'
  | 'shutterstock_videos';

class Analytics {
  private tagManagerArgs: TagManagerArgs = {
    gtmId: GTM_ID,
    dataLayerName: 'dataLayer',
    dataLayer: {
      data: {
        module: 'video_editor'
      }
    }
  };

  private readonly gtmService = AnalyticsService;

  public init() {
    TagManager.initialize(this.tagManagerArgs);
    this.gtmService.init(GTM_ID, GTM_MODULE_ID);
  }

  private sendEvent(
    eventAction: string,
    eventName: 'custom_event' | 'custom_event_non_interaction',
    data: object = {}
  ) {
    const newEvent: {
      event: string;
      data: { module: string; action: string; parameters: object };
    } = {
      event: eventName,
      data: {
        module: 'video_editor',
        action: eventAction,
        parameters: data
      }
    };

    if (!Object.keys(newEvent.data.parameters).length) {
      delete newEvent.data.parameters;
    }

    const newLayer: DataLayerArgs = {
      dataLayerName: this.tagManagerArgs.dataLayerName,
      dataLayer: newEvent
    };

    TagManager.dataLayer(newLayer);
  }

  open(is_subscribed: boolean, source: string) {
    this.sendEvent('video_editor_open', 'custom_event_non_interaction', {
      source,
      is_subscribed,
      braze: true
    });
  }

  upload(item?: 'image' | 'video' | 'music') {
    this.sendEvent('upload_click', 'custom_event', {
      origin: 'video_editor',
      item
    });
  }

  uploadDone(data: { item: 'video' | 'music' | 'image'; result: Result }) {
    this.sendEvent('upload_done', 'custom_event_non_interaction', {
      origin: 'video_editor',
      ...data
    });
  }

  exportPopupOpen(data: { total_price: number; currency: string; is_subscribed: boolean }) {
    this.sendEvent('export_popup_open', 'custom_event', {
      media_type: 'video',
      ...data
    });
  }

  exportPopup(data: {
    action_type: 'close' | 'unlock_gold' | 'download' | 'pay_and_download';
    resolution: Resolution;
    format: VideoFileContainerEnum;
    tool_names: ToolName[];
  }) {
    this.sendEvent('export_popup_action', 'custom_event', {
      ...data,
      media_type: 'video',
      format: data.format.toUpperCase()
    });
  }

  exportPopupOneTimePayment(currency: string, totalAmount: number) {
    this.sendEvent('one_time_purchase_seen', 'custom_event', {
      currency,
      total_price: totalAmount
    });
  }

  exportFinished(data: ExportFinishedData) {
    this.sendEvent('export_finished', 'custom_event_non_interaction', {
      ...omit(['imageSceneCount', 'videoSceneCount'], data),
      media_type: 'video',
      format: data.format.toUpperCase(),
      image_scene_count: data.imageSceneCount,
      video_scene_count: data.videoSceneCount,
      transitions: data.transitions.map(
        (item): Transition => {
          switch (item) {
            case TransitionType.Shape:
              return 'shape';
            case TransitionType.Fade:
              return 'fade';
            case TransitionType.WipeDiagonal:
              return 'wipe';
            default:
              return 'none';
          }
        }
      )
    });
  }

  download(data: { object_id?: string; resolution: string; tool_details: ToolDetails[] }) {
    this.sendEvent('object_export', 'custom_event', {
      ...data,
      source: 'video_editor',
      item: 'video',
      destination: 'local',
      is_remix: false,
      is_private: true,
      is_fte: false,
      braze: true
    });
  }

  videoEditorLoad(data: { loading_time: number; is_logged_in: boolean }) {
    this.sendEvent('video_editor_load', 'custom_event', {
      ...data
    });
  }

  contentLoad(data: {
    size: number;
    format: string;
    download_time: number;
    source: ContentSource;
    result: 'success' | 'cancel';
  }) {
    this.sendEvent('content_load', 'custom_event', {
      ...data
    });
  }

  contentUpload(data: { size: number; format: string; upload_time: number }) {
    this.sendEvent('content_upload', 'custom_event', {
      ...data
    });
  }

  templateLoad(data: { size: number; template_id: string; time: number; load_sequence: number[] }) {
    this.sendEvent('template_load', 'custom_event', {
      ...data
    });
  }

  videoEditItemOpen(item: ToolName) {
    this.sendEvent('video_edit_item_open', 'custom_event', {
      item
    });
  }

  videoEditItemAction(data: {}) {
    this.sendEvent('video_edit_item_action', 'custom_event', {
      action: 'try',
      ...data
    });
  }

  fitAction(aspect_ratio: TargetSettingsAspectRatioEnum) {
    this.videoEditItemAction({
      aspect_ratio,
      item: ToolName.Fit
    });
  }

  sceneFitAction(action: ScalingType) {
    this.videoEditItemAction({
      action,
      item: ToolName.SceneFit
    });
  }

  transitionAction(data: { action?: 'delete'; category_name?: string }) {
    this.videoEditItemAction({
      item: ToolName.Transition,
      ...data
    });
  }

  musicAction(data: {
    is_premium?: boolean;
    item_id?: string;
    result_source?: 'epidemic' | 'local';
    music_duration?: number;
    category_name?: string;
    action?: 'remove';
  }) {
    this.videoEditItemAction({
      item: ToolName.Music,
      ...data
    });
  }

  sceneAction(data: {
    action?: 'delete' | 'add' | 'change';
    item_id?: string;
    length?: number;
    width?: number;
    height?: number;
    size?: number;
    aspect_ratio?: number;
    format?: string;
    media_type?: 'video' | 'image';
    result_source?: 'local' | 'fte' | 'premium' | 'empty';
  }) {
    this.videoEditItemAction({
      item: ToolName.Scene,
      ...data
    });
  }

  trimAction(data: {
    left_moved?: boolean;
    right_moved?: boolean;
    media_type: 'video' | 'photo' | 'audio';
  }) {
    this.videoEditItemAction({
      item: ToolName.Trim,
      left_moved: false,
      right_moved: false,
      ...data
    });
  }

  timelineAction(data: { action?: 'play' | 'pause' }) {
    this.videoEditItemAction({
      item: ToolName.Timeline,
      ...data
    });
  }

  textAction() {
    this.videoEditItemAction({
      item: ToolName.Text
    });
  }

  textPresetAction() {
    this.videoEditItemAction({
      item: ToolName.TextPreset
    });
  }

  fontCollectionsAction() {
    this.videoEditItemAction({
      item: ToolName.FontCollections
    });
  }

  shortcut(data: { item: ToolName; hotkey_command: Shortcuts }) {
    this.sendEvent('keyboard_shortcut', 'custom_event', data);
  }

  templateAction(data: { item_id?: string }) {
    this.videoEditItemAction({
      item: ToolName.Template,
      ...data
    });
  }

  muteAction(action: 'mute' | 'unmute') {
    this.videoEditItemAction({
      item: 'scene',
      action
    });
  }

  videoEditorClose(data: {
    scene_count: number;
    length: number;
    aspect_ratio_changed: TargetSettingsAspectRatioEnum;
  }) {
    this.sendEvent('video_editor_close', 'custom_event_non_interaction', data);
  }

  chooserOpen(drafts_count: number) {
    this.sendEvent('chooser_open', 'custom_event', { drafts_count, screen_name: 'video_editor' });
  }

  chooserClick(position: number) {
    this.sendEvent('chooser_click', 'custom_event', {
      position,
      screen_name: 'video_editor',
      action_button: 'drafts'
    });
  }

  applyRevision(data: { action_button: 'undo' | 'redo'; with_tool: ToolName }) {
    this.sendEvent('page_click', 'custom_event', { screen_name: 'video_editor', ...data });
  }
}

export default new Analytics();
